import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Box
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import dayjs from "dayjs";
import ExportQ from "../Dashboard/ExportQ";
import { LAMBDA_BASE_URL } from "../../../constants";
import { useQueryParams } from "context/QueryParamsContext";
import AddAnswer from "../Dashboard/AddAnswer";
import { enUS } from "date-fns/locale";
import CustomdateD from "../Dashboard/CustomdateD";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useSystemTheme } from "context/SystemThemeContext";

function DataTable({
  entriesPerPageOptions = [5, 10, 15, 20, 25, 50, 100],
  canSearch = false,
  date = false,
  csv = false,
  table,
  pagination = { variant: "gradient", color: "info" },
  isSorted = true,
  noEndBorder = false,
  dashboardDateRange
}) {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const columns = useMemo(() => table.columns, [table]);
  const [rows, setRows] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: 'desc' });
  const { dateRange, setDateRange } = useQueryParams();
  const isFirstLoading = useRef(true);
  const navigate = useNavigate();
  
  const totalPages = useMemo(() => {
    return Math.ceil(totalEntries / entriesPerPage);
  }, [totalEntries, entriesPerPage]);
  
  const start = (page - 1) * entriesPerPage + 1;
  const end = Math.min(page * entriesPerPage, totalEntries);
  
  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;
  
  if (!domainId) {
    navigate("/Access-Denied")
  }

  const location = useLocation();

  useEffect(() => {
    if (
      dashboardDateRange &&
      dashboardDateRange.start_date &&
      dashboardDateRange.end_date
    ) {
      setDateRange(dashboardDateRange);
    }
  }, [dashboardDateRange]);

  const fetchUsers = async () => {
    if (isFirstLoading.current) setLoading(true);
    try {
      const { start_date, end_date } =
        dateRange.start_date && dateRange.end_date
          ? dateRange
          : { start_date: null, end_date: null };
      const offset = (page - 1) * entriesPerPage;
      const res = await axios.get(`${LAMBDA_BASE_URL}/unanswered-questions`, {
        params: {
          limit: entriesPerPage,
          offset,
          search,
          sort_key: sortConfig.key,
          sort_direction: sortConfig.direction,
          start_date,
          end_date
        },
        headers: {
          "DOMAIN-TOKEN": domainId
        }
      });

      const formattedRows = res.data.result.unanswered_questions.map((user) => ({
        id: user.question_id || `default-${user.question}`,
        question: user.question,
        ip_address: user.ip_address,
        created_at: dayjs(user.created_at).format("MMM D, YYYY hh:mm A")
      }));

      setRows(formattedRows);
      setTotalEntries(res.data.result.total || res.data.result.length);
    } catch (err) {
      console.error("error fetching users:", err);
    } finally {
      if (isFirstLoading.current) {
        setLoading(false);
        isFirstLoading.current = false;
      }
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, entriesPerPage, search, sortConfig, dateRange, domainId]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleEntriesPerPageChange = ({ value }) => {
    setEntriesPerPage(value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleSort = (columnKey) => {
    setSortConfig((prevSortConfig) => {
      let newDirection = 'desc';
      if (prevSortConfig.key === columnKey && prevSortConfig.direction === "desc") {
        newDirection = 'asc';
      }
      return { key: columnKey, direction: newDirection };
    });
  };

  const handleDateRangeSelect = ({ start, end }) => {
    setDateRange({ start_date: start, end_date: end });
    localStorage.setItem('saved_start_date', start);
    localStorage.setItem('saved_end_date', end);
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('start_date', start);
    queryParams.set('end_date', end);
    navigate({ pathname: location.pathname, search: queryParams.toString() });
  };

  return (
    <TableContainer sx={{ 
      boxShadow: "none", 
      minHeight: "450px",
      backgroundColor: isDarkMode ? "#060C18" : "transparent",
    }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress sx={{ color: "#219297" }} />
        </Box>
      ) : (
        <>
          <h4 style={{ 
            textAlign: "left", 
            paddingBottom: "0px", 
            paddingLeft: "10px",
            color: isDarkMode ? "#e2e8f0" : "#1a202c",
          }}>Unanswered Questions</h4>
          
          <SoftBox
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            gap={2}
            p={2}
          >
            <SoftBox display="flex" alignItems="center" sx={{ width: { xs: "100%", md: "auto" } }}>
              <SoftSelect
                defaultValue={{ value: entriesPerPage, label: entriesPerPage }}
                options={entriesPerPageOptions.map((entry) => ({
                  value: entry,
                  label: entry,
                }))}
                onChange={handleEntriesPerPageChange}
                size="small"
                sx={{
                  "& .MuiSelect-root": {
                    color: isDarkMode ? "#e2e8f0" : undefined,
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
                    '& fieldset': {
                      borderColor: isDarkMode ? '#4a5568' : '#d2d6da',
                    },
                  },
                }}
              />
              <SoftTypography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "secondary" }} ml={1}>
                entries per page
              </SoftTypography>
            </SoftBox>
            
            <SoftBox
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "center" }}
              gap={2}
              width={{ xs: "100%", md: "auto" }}
            >    
              {canSearch && (
                <SoftBox sx={{ width: { xs: "100%", md: "12rem" } }}>
                  <SoftInput
                    placeholder="Search..."
                    value={search}
                    onChange={handleSearchChange}
                    fullWidth
                    sx={{
                      "& .MuiInputBase-input": {
                        color: isDarkMode ? "#e2e8f0" : "#344767",
                      },
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
                        '& fieldset': {
                          borderColor: isDarkMode ? '#4a5568' : '#d2d6da',
                        },
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: isDarkMode ? "#a0aec0" : "#9e9e9e",
                        opacity: 1,
                      },
                    }}
                  />
                </SoftBox>
              )} 
              {date && (
                <SoftBox sx={{ width: { xs: "100%", md: "auto" }, ml: { xs: 0, md: 0 } }}>
                  <CustomdateD onRangeSelect={handleDateRangeSelect} />
                </SoftBox>
              )}        
              {csv && (
                <SoftBox sx={{ width: { xs: "100%", md: "auto" }, ml: { xs: 0, md: 0 } }}>
                  <ExportQ
                    searchQuery={search}
                    startDate={dateRange.start_date}
                    endDate={dateRange.end_date}
                    filename="ansera_unanswered_questions.csv"
                  />
                </SoftBox>
              )}
            </SoftBox>
          </SoftBox>
          
          <Table>     
            <thead>
              <TableRow>
                {columns.map((column, key) => {
                  const isSortable = !["Content", "Likes/Dislikes"].includes(column.Header);
                  return (
                    <DataTableHeadCell
                      key={column.accessor || key}
                      width={column.width || "auto"}
                      align={column.align || "left"}
                      onClick={isSortable ? () => handleSort(column.accessor) : undefined}
                      sorted={
                        isSortable && sortConfig.key === column.accessor
                          ? sortConfig.direction
                          : "none"
                      }
                      showSortIcon={!["content", "likes_dislikes"].includes(column.accessor)}
                      style={{ 
                        cursor: isSortable ? "pointer" : "default", 
                        userSelect: "none",
                        color: isDarkMode ? "#e2e8f0" : undefined,
                      }}
                    >
                      {column.Header}
                    </DataTableHeadCell>
                  );
                })}
              </TableRow>
            </thead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} sx={{
                  backgroundColor: isDarkMode ? "#060C18" : undefined,
                  "&:hover": {
                    backgroundColor: isDarkMode ? "#2d2d3f" : undefined,
                  },
                }}>
                  <DataTableBodyCell 
                    align="left"
                    sx={{
                      color: isDarkMode ? "#e2e8f0" : undefined,
                      borderBottomColor: isDarkMode ? "#2d2d3f" : undefined,
                    }}
                  >
                    <div style={{ maxWidth: 400 }}>
                      <strong>{row.question}</strong>
                    </div>
                  </DataTableBodyCell>

                  <DataTableBodyCell 
                    align="left"
                    sx={{
                      color: isDarkMode ? "#e2e8f0" : undefined,
                      borderBottomColor: isDarkMode ? "#2d2d3f" : undefined,
                    }}
                  >
                    <a
                      href={`https://whatismyipaddress.com/ip/${row.ip_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        color: isDarkMode ? "#4db6ac" : "#007bff", 
                        textDecoration: "underline" 
                      }}
                    >
                      {row.ip_address}
                    </a>
                  </DataTableBodyCell>

                  <DataTableBodyCell 
                    align="left"
                    sx={{
                      color: isDarkMode ? "#e2e8f0" : undefined,
                      borderBottomColor: isDarkMode ? "#2d2d3f" : undefined,
                    }}
                  >
                    {row.created_at}
                  </DataTableBodyCell>
                  
                  <DataTableBodyCell 
                    align="left"
                    sx={{
                      borderBottomColor: isDarkMode ? "#2d2d3f" : undefined,
                    }}
                  >
                    <div>
                      <AddAnswer id={row.id} />
                    </div>
                  </DataTableBodyCell>
                </TableRow>
              ))}
            </TableBody>
            
            <SoftBox pb={1} display="flex" justifyContent="space-between" alignItems="center" paddingTop="10px" paddingLeft="19px">
              <SoftTypography variant="button" sx={{ color: isDarkMode ? "#a0aec0" : "secondary" }}>
                Showing {start} to {end} of {new Intl.NumberFormat(enUS).format(totalEntries)} entries
              </SoftTypography>
            </SoftBox>
          </Table>

          <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={1}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              shape="circular"
              color="info"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: isDarkMode ? "#e2e8f0" : "#1a202c",
                  "&.Mui-selected": {
                    background: "#219297",
                    color: "white !important", 
                    "&:hover": {
                      background: "#00695c",
                    },
                  },
                  "&:hover": {
                    backgroundColor: isDarkMode ? "#2d2d3f" : undefined,
                  },
                },
              }}
            />
            <SoftTypography variant="button" sx={{ color: isDarkMode ? "#e2e8f0" : "#1a202c" }}>
              Page {page} of {new Intl.NumberFormat(enUS).format(totalPages)}
            </SoftTypography>
          </SoftBox>
        </>
      )}
    </TableContainer>
  );
}

export default DataTable;